/*
 * @Author: TDDC
 * @Date: 2026-02-25
 * @Description: 增强的ICAS登录服务 - 支持客服系统集成
 */

/**
 * ICAS登录配置接口
 * @typedef {Object} IcasLoginConfig
 * @property {string} env - 环境: 'sit', 'uat', 'prod'
 * @property {string} [language='zh-CN'] - 语言: 'zh-CN', 'en-US'等
 * @property {boolean} [wechat=false] - 是否支持微信登录
 * @property {boolean} [whatsapp=false] - 是否支持WhatsApp登录
 * @property {string} [countryCode='CN'] - 国家代码
 * @property {string} [sysCode='IBU-ICAS-CORE'] - 系统代码
 * @property {string} redirectUrl - 登录后重定向URL
 * @property {string} [loadCaptcha='1'] - 是否加载验证码: '0' 不加载, '1' 加载
 */

/**
 * 登录事件回调
 * @typedef {Object} LoginEventCallback
 * @property {Function} onSuccess - 登录成功回调，参数为事件对象
 * @property {Function} [onError] - 登录失败回调，参数为事件对象
 * @property {Function} [onEvent] - 其他事件回调，参数为事件对象
 */

/**
 * 用户信息对象
 * @typedef {Object} UserInfo
 * @property {string} token - 登录token
 * @property {string} phone - 手机号
 * @property {string} encryptedPhone - 加密后的手机号
 * @property {string} loginMethod - 登录方式: 'phone', 'email'等
 * @property {string} userId - 用户ID
 * @property {string} userName - 用户名
 */

export default class IcasLoginService {
  constructor(icasSDK) {
    this.icasSDK = icasSDK
    this.dialog = null
    this.config = {}
    this.userInfo = null
    this.isLoggedIn = false
  }

  /**
   * 检查SDK是否已加载
   * @returns {boolean}
   */
  isSDKLoaded() {
    return this.icasSDK && this.icasSDK.getInstance()
  }

  /**
   * 获取SDK实例
   * @returns {Object|null} window.IcasLoginDialog
   */
  getSDKInstance() {
    return this.icasSDK.getInstance()
  }

  /**
   * 打开登录对话框
   * @param {IcasLoginConfig} config - 登录配置
   * @param {LoginEventCallback} callbacks - 事件回调
   * @returns {Object} 登录对话框实例
   */
  openLoginDialog(config, callbacks = {}) {
    const sdkInstance = this.getSDKInstance()

    if (!sdkInstance) {
      console.error('❌ ICAS SDK未加载，请先初始化SDK')
      if (callbacks.onError) {
        callbacks.onError({
          type: 'sdk_not_loaded',
          message: 'ICAS SDK未加载',
        })
      }
      return null
    }

    // 保存配置
    this.config = config

    // 打开登录对话框
    try {
      this.dialog = sdkInstance.openLoginDialog(config)

      // 绑定事件监听
      this.bindDialogEvents(callbacks)

      return this.dialog
    } catch (error) {
      console.error('❌ 打开登录对话框失败:', error)
      if (callbacks.onError) {
        callbacks.onError({
          type: 'dialog_error',
          message: error.message,
        })
      }
      return null
    }
  }

  /**
   * 绑定对话框事件
   * @private
   * @param {LoginEventCallback} callbacks - 事件回调
   */
  bindDialogEvents(callbacks) {
    if (!this.dialog) return

    // 登录成功事件
    if (callbacks.onSuccess) {
      this.dialog.on('login-success', (event) => {
        console.log('✅ ICAS登录成功:', event)
        // 更新登录状态
        this.handleLoginSuccess(event)
        callbacks.onSuccess(event)
      })
    }

    // 登录失败和其他事件
    if (callbacks.onEvent) {
      this.dialog.on('login-event', (event) => {
        console.log('📍 登录事件:', event)

        if (event.type === 'failed') {
          console.error('❌ ICAS登录失败')
        } else if (event.type === 'opened') {
          console.log(`🔗 打开其他登录方式: ${event.data?.type || '未知'}`)
        }

        callbacks.onEvent(event)
      })
    }

    // 如果没有提供onEvent但提供了onError，自动处理失败事件
    if (!callbacks.onEvent && callbacks.onError) {
      this.dialog.on('login-event', (event) => {
        if (event.type === 'failed') {
          console.error('❌ ICAS登录失败')
          callbacks.onError(event)
        }
      })
    }
  }

  /**
   * 处理登录成功
   * @private
   * @param {Object} event - 登录成功事件对象
   */
  handleLoginSuccess(event) {
    const userInfo = this.getUserInfoFromEvent(event)
    if (userInfo) {
      this.isLoggedIn = true
      this.userInfo = {
        ...userInfo,
        loginMethod: 'phone', // ICAS 默认手机号登录
        encryptedPhone: userInfo.encryptedPhone || null,
      }
      console.log('✅ 用户信息已更新:', this.userInfo)
    }
  }

  /**
   * 获取登录成功后的Token
   * @param {Object} event - 登录成功事件对象
   * @returns {string|null} Token字符串
   */
  getTokenFromEvent(event) {
    if (event && event.data && event.data.token) {
      return event.data.token
    }
    return null
  }

  /**
   * 获取登录用户信息
   * @param {Object} event - 登录成功事件对象
   * @returns {Object|null} 用户信息对象
   */
  getUserInfoFromEvent(event) {
    if (event && event.data) {
      return event.data.phoneNumberSensitization
    }
    return null
  }

  /**
   * 获取加密后的手机号
   * @param {Object} event - 登录成功事件对象
   * @returns {string|null} 加密后的手机号
   */
  getEncryptedPhoneFromEvent(event) {
    const userInfo = this.getUserInfoFromEvent(event)
    if (userInfo && userInfo.encryptedPhone) {
      return userInfo.encryptedPhone
    }
    // 如果事件对象中直接包含 encryptedPhone
    if (event && event.encryptedPhone) {
      return event.encryptedPhone
    }
    return null
  }

  /**
   * 获取当前用户信息
   * @returns {Object|null} 用户信息
   */
  getCurrentUserInfo() {
    return this.userInfo
  }

  /**
   * 获取当前加密手机号
   * @returns {string|null} 加密后的手机号
   */
  getEncryptedPhone() {
    return this.userInfo?.encryptedPhone || null
  }

  /**
   * 检查用户是否登录
   * @returns {boolean}
   */
  isLogin() {
    return this.isLoggedIn && this.userInfo
  }

  /**
   * 检查是否为手机号登录
   * @returns {boolean}
   */
  isPhoneLogin() {
    return this.isLogin() && this.userInfo?.loginMethod === 'phone'
  }

  /**
   * 关闭登录对话框
   */
  closeDialog() {
    if (this.dialog && typeof this.dialog.close === 'function') {
      this.dialog.close()
      this.dialog = null
    }
  }

  /**
   * 获取当前对话框实例
   * @returns {Object|null}
   */
  getDialog() {
    return this.dialog
  }

  /**
   * 清理资源
   */
  destroy() {
    this.closeDialog()
    this.config = {}
    this.userInfo = null
    this.isLoggedIn = false
  }

  /**
   * 清除登录信息（登出）
   */
  clearLoginInfo() {
    this.userInfo = null
    this.isLoggedIn = false
    console.log('✅ 登录信息已清除')
  }
}
